// contexts/LocaleContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AvailableLocales } from '../../translations/index';

type Locale = AvailableLocales;

type LocaleContextType = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
};

const DEFAULT_LOCALE = 'ru';

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

function getBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LOCALE;
  }

  const browserLang = navigator.language.split('-')[0];
  const supportedLocales: Locale[] = ['en', 'ru'];
  
  return supportedLocales.includes(browserLang as Locale) 
    ? (browserLang as Locale) 
    : DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    return (localStorage.getItem('locale') as Locale) || getBrowserLocale();
  });

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
  };

  // Синхронизация с другими вкладками
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'locale' && e.newValue) {
        setLocaleState(e.newValue as Locale);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);


  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );

}

export function useLocale() {
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  
  return context;
}